import { useState, useEffect, useCallback, useRef } from 'react';

interface ExchangeRate {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  rate: number;
  validUntil: number;
  isLoading: boolean;
  error: string | null;
}

interface UseExchangeRateOptions {
  refreshInterval?: number;
  autoRefresh?: boolean;
  minRefreshInterval?: number;
  demoMode?: boolean;
}

// Mock exchange rates for demo mode
const MOCK_RATES: Record<string, number> = {
  'btc_usdt_eth': 95000,
  'btc_eth': 28.5,
  'eth_usdt_eth': 3400,
  'xmr_btc': 0.0045,
  'xmr_eth': 0.13,
  'xmr_usdt_eth': 420,
  'sol_btc': 0.0028,
  'sol_eth': 0.085,
  'sol_usdt_eth': 280,
  'ltc_btc': 0.0025,
  'ltc_eth': 0.072,
  'ltc_usdt_eth': 240,
  'bnb_btc': 0.0095,
  'bnb_eth': 0.28,
  'bnb_usdt_eth': 950,
};

const getMockRate = (from: string, to: string): number => {
  const key = `${from}_${to}`;
  if (MOCK_RATES[key]) return MOCK_RATES[key];

  // Generate plausible mock rate
  const baseRates: Record<string, number> = {
    btc: 95000,
    eth: 3400,
    xmr: 420,
    sol: 280,
    ltc: 240,
    bnb: 950,
    usdt_eth: 1,
    usdc_eth: 1,
  };

  const fromRate = baseRates[from] || 100;
  const toRate = baseRates[to] || 100;

  return (fromRate / toRate) * (0.98 + Math.random() * 0.04);
};

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api';

export const useExchangeRate = (
  initialFromCurrency: string = '',
  initialToCurrency: string = '',
  initialAmount: number = 0,
  options: UseExchangeRateOptions = {}
) => {
  const {
    refreshInterval = 10000,
    autoRefresh = true,
    minRefreshInterval = 2000,
    demoMode = false // Use real backend data
  } = options;

  const [exchangeRate, setExchangeRate] = useState<ExchangeRate>({
    fromCurrency: initialFromCurrency || 'btc',
    toCurrency: initialToCurrency || 'usdt_eth',
    fromAmount: initialAmount || 0,
    toAmount: 0,
    rate: 0,
    validUntil: Date.now(),
    isLoading: false,
    error: null
  });

  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(autoRefresh);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<number>(0);
  const activeRequestRef = useRef<AbortController | null>(null);

  const fetchExchangeRate = useCallback(async (
    fromCurrency: string,
    toCurrency: string,
    amount: number,
    skipCache: boolean = false
  ): Promise<void> => {
    if (!fromCurrency || !toCurrency || amount <= 0) {
      setExchangeRate(prev => ({
        ...prev,
        toAmount: 0,
        rate: 0,
        isLoading: false,
        error: null
      }));
      return;
    }

    const now = Date.now();
    if (!skipCache && now - lastFetchRef.current < minRefreshInterval) {
      return;
    }

    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
    }

    activeRequestRef.current = new AbortController();
    lastFetchRef.current = now;

    setExchangeRate(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      // Demo mode - use mock rates
      if (demoMode) {
        await new Promise(resolve => setTimeout(resolve, 300)); // Simulate network delay

        const mockRate = getMockRate(fromCurrency.toLowerCase(), toCurrency.toLowerCase());
        const toAmount = amount * mockRate;

        setExchangeRate(prev => ({
          ...prev,
          fromCurrency,
          toCurrency,
          fromAmount: amount,
          toAmount,
          rate: mockRate,
          validUntil: Date.now() + 30000,
          isLoading: false,
          error: null
        } as ExchangeRate));

        return;
      }

      // Real API call (for when backend is available)
      const response = await fetch(`${API_URL}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromCurrency: fromCurrency.toLowerCase(),
          toCurrency: toCurrency.toLowerCase(),
          fromAmount: amount,
        }),
        signal: activeRequestRef.current.signal
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setExchangeRate(prev => ({
            ...prev,
            fromCurrency,
            toCurrency,
            fromAmount: amount,
            toAmount: parseFloat(data.data.toAmount) || 0,
            rate: parseFloat(data.data.rate) || 0,
            validUntil: Date.now() + 30000,
            isLoading: false,
            error: null
          }));
          return;
        }
      }

      throw new Error('API request failed');

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      // Fallback to mock rate on error
      const mockRate = getMockRate(fromCurrency.toLowerCase(), toCurrency.toLowerCase());
      const toAmount = amount * mockRate;

      setExchangeRate(prev => ({
        ...prev,
        fromCurrency,
        toCurrency,
        fromAmount: amount,
        toAmount,
        rate: mockRate,
        validUntil: Date.now() + 60000,
        isLoading: false,
        error: null
      } as ExchangeRate));
    }
  }, [minRefreshInterval, demoMode]);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedFetchRate = useCallback((
    fromCurrency: string,
    toCurrency: string,
    amount: number
  ) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchExchangeRate(fromCurrency, toCurrency, amount);
    }, 200);
  }, [fetchExchangeRate]);

  const updateExchange = useCallback((
    fromCurrency: string,
    toCurrency: string,
    amount: number,
    immediate: boolean = false
  ) => {
    if (immediate) {
      fetchExchangeRate(fromCurrency, toCurrency, amount, true);
    } else {
      debouncedFetchRate(fromCurrency, toCurrency, amount);
    }
  }, [fetchExchangeRate, debouncedFetchRate]);

  const refreshRates = useCallback(() => {
    if (exchangeRate.fromCurrency && exchangeRate.toCurrency && exchangeRate.fromAmount > 0) {
      fetchExchangeRate(
        exchangeRate.fromCurrency,
        exchangeRate.toCurrency,
        exchangeRate.fromAmount,
        true
      );
    }
  }, [exchangeRate, fetchExchangeRate]);

  const toggleAutoRefresh = useCallback((enabled: boolean) => {
    setIsAutoRefreshEnabled(enabled);
  }, []);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (isAutoRefreshEnabled &&
        exchangeRate.fromCurrency &&
        exchangeRate.toCurrency &&
        exchangeRate.fromAmount > 0) {

      intervalRef.current = setInterval(() => {
        refreshRates();
      }, refreshInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    isAutoRefreshEnabled,
    exchangeRate.fromCurrency,
    exchangeRate.toCurrency,
    exchangeRate.fromAmount,
    refreshInterval,
    refreshRates
  ]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }
    };
  }, []);

  return {
    exchangeRate,
    updateExchange,
    refreshRates,
    toggleAutoRefresh,
    isAutoRefreshEnabled
  };
};
